'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Copy, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { jwtDecoderContent } from '../../data/jwt-decoder-content';
import Link from 'next/link';

type JwtPayload = {
    header: Record<string, any>;
    payload: Record<string, any>;
    signature: string;
};

export default function JwtDecoder() {
    const { toast } = useToast();
    const [jwt, setJwt] = useState('');
    const [decoded, setDecoded] = useState<JwtPayload | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const decodeJwt = useCallback((token: string): JwtPayload | null => {
        try {
            const [header, payload, signature] = token.split('.');
            if (!header || !payload || !signature) {
                throw new Error('Invalid JWT format');
            }

            const decodeBase64 = (str: string): string => {
                try {
                    // Replace URL-safe characters and add padding if needed
                    const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
                    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
                    return decodeURIComponent(
                        atob(padded)
                            .split('')
                            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                            .join('')
                    );
                } catch (e) {
                    console.error('Error decoding base64:', e);
                    throw new Error('Failed to decode JWT component');
                }
            };

            return {
                header: JSON.parse(decodeBase64(header)),
                payload: JSON.parse(decodeBase64(payload)),
                signature: signature
            };
        } catch (error) {
            console.error('Error decoding JWT:', error);
            return null;
        }
    }, []);

    const handleDecode = useCallback(() => {
        if (!jwt.trim()) {
            toast({
                title: 'Error',
                description: 'Please enter a JWT token',
                variant: 'destructive',
            });
            return;
        }

        setIsLoading(true);
        try {
            const result = decodeJwt(jwt.trim());
            if (result) {
                setDecoded(result);
            } else {
                toast({
                    title: 'Error',
                    description: 'Failed to decode JWT. Please check the token and try again.',
                    variant: 'destructive',
                });
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'An error occurred while decoding the JWT.',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    }, [jwt, decodeJwt, toast]);

    const copyToClipboard = useCallback((text: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
    }, [toast]);

    const formatJson = (obj: Record<string, any>): string => {
        return JSON.stringify(obj, null, 2);
    };

    const clearAll = useCallback(() => {
        setJwt('');
        setDecoded(null);
    }, []);

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <ToolPageIntro title="JWT Decoder">
                Decode JWT (JSON Web Tokens) instantly with our free online JWT decoder. View payload, header, and signature information for debugging and development.
            </ToolPageIntro>
                    </div>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>JWT Token</CardTitle>
                        <CardDescription>
                            Paste your JWT token to decode and inspect its contents
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="jwt">JWT Token</Label>
                                <div className="flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={clearAll}
                                        disabled={!jwt && !decoded}
                                    >
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Clear
                                    </Button>
                                </div>
                            </div>
                            <Textarea
                                id="jwt"
                                placeholder="Paste your JWT token here (e.g., eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c)"
                                className="min-h-[120px] font-mono text-sm"
                                value={jwt}
                                onChange={(e) => setJwt(e.target.value)}
                            />
                        </div>

                        <div className="flex justify-end">
                            <Button onClick={handleDecode} disabled={!jwt.trim() || isLoading}>
                                {isLoading ? 'Decoding...' : 'Decode JWT'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {decoded && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Decoded JWT</CardTitle>
                            <CardDescription>Inspect the contents of your JWT token</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="payload" className="w-full">
                                <TabsList className="grid w-full grid-cols-3">
                                    <TabsTrigger value="header">Header</TabsTrigger>
                                    <TabsTrigger value="payload">Payload</TabsTrigger>
                                    <TabsTrigger value="signature">Signature</TabsTrigger>
                                </TabsList>

                                <TabsContent value="header" className="mt-4">
                                    <div className="relative rounded-md bg-muted p-4">
                                        <pre className="overflow-x-auto text-sm">
                                            <code>{formatJson(decoded.header)}</code>
                                        </pre>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-2 top-2 h-8 w-8 p-0"
                                            onClick={() => copyToClipboard(formatJson(decoded.header))}
                                        >
                                            <Copy className="h-4 w-4" />
                                            <span className="sr-only">Copy</span>
                                        </Button>
                                    </div>
                                </TabsContent>

                                <TabsContent value="payload" className="mt-4">
                                    <div className="relative rounded-md bg-muted p-4">
                                        <pre className="overflow-x-auto text-sm">
                                            <code>{formatJson(decoded.payload)}</code>
                                        </pre>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-2 top-2 h-8 w-8 p-0"
                                            onClick={() => copyToClipboard(formatJson(decoded.payload))}
                                        >
                                            <Copy className="h-4 w-4" />
                                            <span className="sr-only">Copy</span>
                                        </Button>
                                    </div>
                                </TabsContent>

                                <TabsContent value="signature" className="mt-4">
                                    <div className="relative rounded-md bg-muted p-4">
                                        <pre className="overflow-x-auto break-all text-sm">
                                            <code>{decoded.signature}</code>
                                        </pre>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-2 top-2 h-8 w-8 p-0"
                                            onClick={() => copyToClipboard(decoded.signature)}
                                        >
                                            <Copy className="h-4 w-4" />
                                            <span className="sr-only">Copy</span>
                                        </Button>
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={jwtDecoderContent.title}
                    description={jwtDecoderContent.description}
                    content={jwtDecoderContent.content}
                    faqs={jwtDecoderContent.faqs}
                    relatedTools={jwtDecoderContent.relatedTools}
                />
            </div>
        </div>
    );
}
